import type { $TSFixMe } from '@calefy-inc/utilityTypes';

/**
 * Recursively copies values from a source path to a target path in a form
 * @param formApi - The informed form API instance
 * @param sourcePath - The full path to the source field
 * @param targetPath - The full path to the target field
 * @param value - The value to copy
 * @returns boolean - true if copy was successful, false otherwise
 */
export const copyFormValues = (
  formApi: $TSFixMe,
  sourcePath: string,
  targetPath: string,
  value: any,
): boolean => {
  try {
    if (value === null || value === undefined) {
      formApi.setValue(targetPath, value);
      return true;
    } else if (Array.isArray(value)) {
      // For arrays, copy each element recursively
      const results = value.map((arrayItem, arrayIndex) =>
        copyFormValues(
          formApi,
          `${sourcePath}[${arrayIndex}]`,
          `${targetPath}[${arrayIndex}]`,
          arrayItem,
        ),
      );
      return results.every(Boolean);
    } else if (typeof value === 'object') {
      // For objects, copy each property recursively
      const results = Object.entries(value).map(([key, nestedValue]) =>
        copyFormValues(
          formApi,
          `${sourcePath}.${key}`,
          `${targetPath}.${key}`,
          nestedValue,
        ),
      );
      return results.every(Boolean);
    } else {
      // For primitives, copy directly
      formApi.setValue(targetPath, value);
      return true;
    }
  } catch (error) {
    console.error(
      `Failed to copy value from ${sourcePath} to ${targetPath}:`,
      error,
    );
    return false;
  }
};

/**
 * Copies array values from source to destination indices
 * @param formApi - The informed form API instance
 * @param sourceArray - The source array data
 * @param sourceFieldName - The base field name for source (e.g., 'risk_address[0].misc_coverage')
 * @param targetFieldName - The base field name for target (e.g., 'risk_address[1].misc_coverage')
 * @returns { success: boolean; errors: string[] }
 */
export const copyArrayFormValues = (
  formApi: $TSFixMe,
  sourceArray: any[],
  sourceFieldName: string,
  targetFieldName: string,
): { success: boolean; errors: string[] } => {
  const errors: string[] = [];

  try {
    const results = sourceArray.map((sourceItem, sourceIndex) => {
      const sourceItemPath = `${sourceFieldName}[${sourceIndex}]`;
      const targetItemPath = `${targetFieldName}[${sourceIndex}]`;

      const success = copyFormValues(
        formApi,
        sourceItemPath,
        targetItemPath,
        sourceItem,
      );

      if (!success) {
        errors.push(
          `Failed to copy from ${sourceItemPath} to ${targetItemPath}`,
        );
      }

      return success;
    });

    const allSuccessful = results.every(Boolean);

    return {
      success: allSuccessful,
      errors: errors,
    };
  } catch (error) {
    const errorMessage = `Failed to copy array values: ${
      error instanceof Error ? error.message : String(error)
    }`;
    console.error(errorMessage);
    errors.push(errorMessage);

    return {
      success: false,
      errors: errors,
    };
  }
};
